<template>
  <div class="ob-root">

    <!-- ══════════ Left: Branding ══════════ -->
    <aside class="ob-left">
      <div class="ob-orb ob-orb--a"></div>
      <div class="ob-orb ob-orb--b"></div>
      <div class="ob-orb ob-orb--c"></div>
      <div class="ob-ring ob-ring--1"></div>
      <div class="ob-ring ob-ring--2"></div>
      <div class="ob-ring ob-ring--3"></div>

      <div class="ob-brand">
        <div class="ob-logo-box">
          <span class="mdi mdi-storefront-outline"></span>
        </div>
        <h1 class="ob-brand-name">SALAMANDA</h1>
        <p class="ob-brand-sub">{{ $t('onboard.brand_tagline') }}</p>
      </div>

      <ul class="ob-feats">
        <li v-for="f in features" :key="f.icon" class="ob-feat">
          <div class="ob-feat-ico"><span :class="`mdi ${f.icon}`"></span></div>
          <div>
            <div class="ob-feat-t">{{ $t(f.titleKey) }}</div>
            <div class="ob-feat-d">{{ $t(f.descKey) }}</div>
          </div>
        </li>
      </ul>

      <p class="ob-foot">{{ $t('onboard.version') }}</p>
    </aside>

    <!-- ══════════ Right: Wizard ══════════ -->
    <main class="ob-right">

      <!-- Progress stepper (hidden while connecting) -->
      <div class="ob-stepper" v-if="step !== 'connecting'">
        <div class="ob-step" :class="{ 'ob-step--active': currentStepIndex === 0, 'ob-step--done': currentStepIndex > 0 }">
          <div class="ob-sdot">
            <span v-if="currentStepIndex > 0" class="mdi mdi-check"></span>
            <span v-else>1</span>
          </div>
          <span class="ob-slabel">{{ $t('onboard.step_server') }}</span>
        </div>
        <div class="ob-sline" :class="{ 'ob-sline--done': currentStepIndex > 0 }"></div>
        <div class="ob-step" :class="{ 'ob-step--active': currentStepIndex === 1, 'ob-step--done': currentStepIndex > 1 }">
          <div class="ob-sdot">
            <span v-if="currentStepIndex > 1" class="mdi mdi-check"></span>
            <span v-else>2</span>
          </div>
          <span class="ob-slabel">{{ $t('onboard.step_license') }}</span>
        </div>
        <div class="ob-sline" :class="{ 'ob-sline--done': currentStepIndex > 1 }"></div>
        <div class="ob-step" :class="{ 'ob-step--active': currentStepIndex === 2, 'ob-step--done': currentStepIndex > 2 }">
          <div class="ob-sdot">
            <span v-if="currentStepIndex > 2" class="mdi mdi-check"></span>
            <span v-else>3</span>
          </div>
          <span class="ob-slabel">{{ $t('onboard.step_done') }}</span>
        </div>
      </div>

      <!-- Step panels with slide transitions -->
      <transition name="ob-slide" mode="out-in">

        <!-- ① Server Setup -->
        <div v-if="step === 'server'" key="server" class="ob-panel">
          <div class="ob-pico ob-pico--indigo">
            <span class="mdi mdi-server-network"></span>
          </div>
          <h2 class="ob-ptitle">{{ $t('onboard.server.title') }}</h2>
          <p class="ob-psub">{{ $t('onboard.server.subtitle') }}</p>

          <div class="ob-fgroup">
            <label class="ob-flabel">{{ $t('onboard.server.url_label') }}</label>
            <div class="ob-finput" :class="{ 'ob-finput--err': serverError }">
              <span class="mdi mdi-link-variant ob-ficon"></span>
              <input
                ref="urlInput"
                v-model.trim="serverUrl"
                type="url"
                class="ob-finput-el"
                :placeholder="$t('onboard.server.url_placeholder')"
                autocomplete="url"
                spellcheck="false"
                @keyup.enter="connectServer"
              />
            </div>
            <p v-if="serverError" class="ob-ferr">
              <span class="mdi mdi-alert-circle-outline"></span>
              {{ serverError }}
            </p>
            <p v-else class="ob-fhint">{{ $t('onboard.server.url_hint') }}</p>
          </div>

          <button class="ob-btn ob-btn--primary" :disabled="!serverUrl || connecting" @click="connectServer">
            <span v-if="connecting" class="mdi mdi-loading ob-spin"></span>
            <span v-else class="mdi mdi-arrow-right-circle-outline"></span>
            {{ connecting ? $t('onboard.server.btn_connecting') : $t('onboard.server.btn_connect') }}
          </button>
        </div>

        <!-- ② Connecting / Verifying -->
        <div v-else-if="step === 'connecting'" key="connecting" class="ob-panel ob-panel--c">
          <div class="ob-pulse">
            <div class="ob-pr ob-pr--1"></div>
            <div class="ob-pr ob-pr--2"></div>
            <div class="ob-pico ob-pico--indigo ob-pico--lg">
              <span class="mdi mdi-server-network"></span>
            </div>
          </div>
          <h2 class="ob-ptitle ob-mt6">{{ $t('onboard.connecting.title') }}</h2>
          <p class="ob-psub ob-psub--nb">{{ $t('onboard.connecting.subtitle') }}</p>
          <div class="ob-loader"><div class="ob-loader-fill"></div></div>
        </div>

        <!-- ③ License Activation -->
        <div v-else-if="step === 'license'" key="license" class="ob-panel">
          <div class="ob-pico ob-pico--amber">
            <span class="mdi mdi-shield-key-outline"></span>
          </div>
          <h2 class="ob-ptitle">{{ $t('onboard.license.title') }}</h2>
          <p class="ob-psub ob-psub--sm">{{ $t('onboard.license.subtitle') }}</p>

          <el-alert
            v-if="status.message"
            :type="status.type || 'info'"
            :title="status.message"
            show-icon
            closable
            class="ob-alert"
            @close="status.message = null"
          />

          <!-- Method toggle -->
          <div class="ob-mswitch">
            <button class="ob-mbtn" :class="{ 'ob-mbtn--on': licenseMethod === 'account' }" @click="setMethod('account')">
              <span class="mdi mdi-account-circle-outline"></span>
              {{ $t('onboard.license.tab_account') }}
            </button>
            <button class="ob-mbtn" :class="{ 'ob-mbtn--on': licenseMethod === 'token' }" @click="setMethod('token')">
              <span class="mdi mdi-key-outline"></span>
              {{ $t('onboard.license.tab_code') }}
            </button>
          </div>

          <!-- Account credentials -->
          <transition name="ob-fade" mode="out-in">
            <div v-if="licenseMethod === 'account'" key="acc">
              <div class="ob-fgroup">
                <label class="ob-flabel">{{ $t('onboard.license.username_label') }}</label>
                <div class="ob-finput">
                  <span class="mdi mdi-account-outline ob-ficon"></span>
                  <input
                    ref="usernameInput"
                    v-model="lc.username"
                    type="text"
                    class="ob-finput-el"
                    :placeholder="$t('onboard.license.username_placeholder')"
                    autocomplete="username"
                    @keyup.enter="activate"
                  />
                </div>
              </div>
              <div class="ob-fgroup">
                <label class="ob-flabel">{{ $t('onboard.license.password_label') }}</label>
                <div class="ob-finput">
                  <span class="mdi mdi-lock-outline ob-ficon"></span>
                  <input
                    v-model="lc.password"
                    :type="showPass ? 'text' : 'password'"
                    class="ob-finput-el ob-finput-el--pass"
                    placeholder="••••••••"
                    autocomplete="current-password"
                    @keyup.enter="activate"
                  />
                  <button class="ob-eye" @click="showPass = !showPass" tabindex="-1" type="button">
                    <span :class="`mdi mdi-eye${showPass ? '-off' : ''}-outline`"></span>
                  </button>
                </div>
              </div>
              <button class="ob-btn ob-btn--primary" :disabled="loading || !lc.username || !lc.password" @click="activate">
                <span v-if="loading" class="mdi mdi-loading ob-spin"></span>
                <span v-else class="mdi mdi-shield-check-outline"></span>
                {{ loading ? $t('onboard.license.btn_activating') : $t('onboard.license.btn_activate') }}
              </button>
            </div>

            <!-- Activation code -->
            <div v-else key="tok">
              <div class="ob-fgroup">
                <label class="ob-flabel">{{ $t('onboard.license.code_label') }}</label>
                <textarea
                  ref="tokenInput"
                  v-model="lc.activation_token"
                  class="ob-textarea"
                  rows="5"
                  :placeholder="$t('onboard.license.code_placeholder')"
                ></textarea>
              </div>
              <button class="ob-btn ob-btn--primary" :disabled="loading || !lc.activation_token" @click="activate">
                <span v-if="loading" class="mdi mdi-loading ob-spin"></span>
                <span v-else class="mdi mdi-key-variant"></span>
                {{ loading ? $t('onboard.license.btn_activating') : $t('onboard.license.btn_activate_code') }}
              </button>
            </div>
          </transition>

          <button class="ob-btn ob-btn--ghost ob-mt4" @click="goToServer">
            <span class="mdi mdi-arrow-left"></span>
            {{ $t('onboard.license.btn_change_server') }}
          </button>
        </div>

        <!-- ④ Success -->
        <div v-else-if="step === 'success'" key="success" class="ob-panel ob-panel--c">
          <div class="ob-success">
            <div class="ob-success-bg"></div>
            <span class="mdi mdi-check-circle ob-success-ico"></span>
          </div>
          <h2 class="ob-ptitle ob-mt5">{{ $t('onboard.success.title') }}</h2>
          <p class="ob-psub ob-psub--nb">{{ $t('onboard.success.subtitle') }}</p>
          <div class="ob-dots">
            <span></span><span></span><span></span>
          </div>
        </div>

      </transition>
    </main>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import axios from 'axios'

const createLicenseHttpClient = () =>
  axios.create({
    baseURL: 'https://license.salamanda.org/',
    timeout: 8000,
    withCredentials: true, // required so the XSRF-TOKEN cookie is stored and echoed back as X-XSRF-TOKEN
    headers: { Accept: 'application/json' }
  })

export default {
  name: 'OnboardBase',

  data () {
    return {
      step: 'server',
      serverUrl: '',
      serverError: '',
      connecting: false,
      licenseMethod: 'account',
      showPass: false,
      lc: {
        secret: 'salamanda-v2',
        type: 'account',
        activation_token: '',
        username: '',
        password: ''
      },
      status: { type: '', message: null },
      loading: false,
      features: [
        { icon: 'mdi-point-of-sale', titleKey: 'onboard.features.pos_title', descKey: 'onboard.features.pos_desc' },
        { icon: 'mdi-package-variant-closed', titleKey: 'onboard.features.inventory_title', descKey: 'onboard.features.inventory_desc' },
        { icon: 'mdi-chart-line', titleKey: 'onboard.features.analytics_title', descKey: 'onboard.features.analytics_desc' },
        { icon: 'mdi-store-multiple-outline', titleKey: 'onboard.features.multistore_title', descKey: 'onboard.features.multistore_desc' }
      ]
    }
  },

  computed: {
    ...mapState({
      baseUrl: state => state.Base.BASE_URL
    }),

    currentStepIndex () {
      const map = { server: 0, connecting: 1, license: 1, success: 2 }
      return map[this.step] ?? 0
    }
  },

  methods: {
    ...mapActions('Base', ['setBaseURL']),

    getErrorMessage (err, fallback) {
      if (err?.response?.data?.message) return err.response.data.message
      if (err?.response?.status) return `Error ${err.response.status}: ${err.response.statusText || 'Request failed'}`
      if (err?.message) return err.message
      return fallback
    },

    setStatus (type, message) {
      this.status = { type, message }
    },

    setMethod (method) {
      this.licenseMethod = method
      this.lc.type = method
      this.$nextTick(() => {
        if (method === 'account' && this.$refs.usernameInput) this.$refs.usernameInput.focus()
        if (method === 'token' && this.$refs.tokenInput) this.$refs.tokenInput.focus()
      })
    },

    goToServer () {
      this.serverUrl = this.baseUrl || ''
      this.serverError = ''
      this.status.message = null
      this.step = 'server'
      this.$nextTick(() => {
        if (this.$refs.urlInput) this.$refs.urlInput.focus()
      })
    },

    async connectServer () {
      if (!this.serverUrl) return
      this.serverError = ''
      this.connecting = true
      try {
        const res = await this.$http.post('ping', {}, { baseURL: this.serverUrl })
        if (res.data.data === 'pong') {
          await this.setBaseURL(this.serverUrl)
          this.step = 'connecting'
          await this.verifyLicense()
        } else {
          this.serverError = this.$t('onboard.server.err_verify')
        }
      } catch {
        this.serverError = this.$t('onboard.server.err_unreachable')
      } finally {
        this.connecting = false
      }
    },

    async verifyLicense () {
      try {
        await this.$authService.verifyLicense()
        this.step = 'success'
        setTimeout(() => this.$router.push({ name: 'login' }), 2500)
      } catch (err) {
        this.step = 'license'
        this.setStatus('warning', this.getErrorMessage(err, this.$t('onboard.license.err_verify_failed')))
        this.$nextTick(() => {
          if (this.$refs.usernameInput) this.$refs.usernameInput.focus()
        })
      }
    },

    async activate () {
      this.status.message = null
      this.loading = true
      this.lc.type = this.licenseMethod

      const http = createLicenseHttpClient()

      try {
        await http.get('sanctum/csrf-cookie')

        const loginRes = await http.post('api/v1/login', this.lc)
        const token = loginRes.data.data.token

        const licenseRes = await http.get('api/v1/license', {
          headers: { Authorization: `Bearer ${token}` }
        })

        const license = licenseRes.data

        try {
          await this.$authService.setLicense(license, this.baseUrl)
          this.loading = false
          this.step = 'success'
          setTimeout(() => this.$router.push({ name: 'login' }), 2500)
        } catch (err) {
          this.setStatus('warning', this.getErrorMessage(err, this.$t('onboard.license.err_save_failed')))
          this.loading = false
        }
      } catch (err) {
        const is502 = err?.response?.status === 502
        this.setStatus(
          is502 ? 'warning' : 'error',
          is502
            ? this.$t('onboard.license.err_no_license')
            : this.getErrorMessage(err, this.$t('onboard.license.err_activation_failed'))
        )
        this.loading = false
      }
    }
  },

  created () {
    // At onboarding time the user has no stored locale yet.
    // Honour the browser / OS language preference if we support it.
    const stored = this.$store.getters['Auth/getLocale']
    if (!stored) {
      const browserLang = (navigator.languages?.[0] || navigator.language || 'en').slice(0, 2).toLowerCase()
      const supported = ['en', 'fr']
      const resolved = supported.includes(browserLang) ? browserLang : 'en'
      this.$i18n.locale = resolved
    }
  },

  async mounted () {
    if (this.baseUrl && this.baseUrl.length) {
      this.serverUrl = this.baseUrl
      this.step = 'connecting'
      await this.verifyLicense()
    } else {
      this.step = 'server'
      this.$nextTick(() => {
        if (this.$refs.urlInput) this.$refs.urlInput.focus()
      })
    }
  }
}
</script>

<style scoped>
/* ─── Root ─────────────────────────────────────────────── */
.ob-root {
  display: flex;
  min-height: 100vh;
  font-family: 'Inter', 'Helvetica Neue', Arial, sans-serif;
}

/* ─── Left Panel ───────────────────────────────────────── */
.ob-left {
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 400px;
  min-width: 340px;
  min-height: 100vh;
  padding: 48px 40px;
  background: linear-gradient(145deg, #3730a3 0%, #4f46e5 28%, #6d28d9 55%, #2563eb 80%, #1d4ed8 100%);
  overflow: hidden;
  color: #fff;
  flex-shrink: 0;
}

@media (max-width: 900px) {
  .ob-left { display: none; }
}

/* Orbs */
.ob-orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(60px);
  pointer-events: none;
}
.ob-orb--a {
  width: 280px; height: 280px;
  top: -80px; left: -60px;
  background: radial-gradient(circle, rgba(255,255,255,0.22) 0%, transparent 70%);
  animation: obFloat 8s ease-in-out infinite;
}
.ob-orb--b {
  width: 200px; height: 200px;
  bottom: 120px; right: -40px;
  background: radial-gradient(circle, rgba(192,132,252,0.45) 0%, transparent 70%);
  animation: obFloat 11s ease-in-out infinite reverse;
}
.ob-orb--c {
  width: 160px; height: 160px;
  bottom: -40px; left: 60px;
  background: radial-gradient(circle, rgba(96,165,250,0.4) 0%, transparent 70%);
  animation: obFloat 9s ease-in-out infinite 2s;
}
@keyframes obFloat {
  0%, 100% { transform: translateY(0) scale(1); }
  50% { transform: translateY(-20px) scale(1.05); }
}

/* Rings */
.ob-ring {
  position: absolute;
  border-radius: 50%;
  border: 1px solid rgba(255,255,255,0.15);
  pointer-events: none;
}
.ob-ring--1 { width: 300px; height: 300px; top: -120px; right: -80px; }
.ob-ring--2 { width: 180px; height: 180px; bottom: 80px; left: -60px; }
.ob-ring--3 { width: 100px; height: 100px; top: 40%; right: 30px; }

/* Brand */
.ob-brand { position: relative; z-index: 1; }
.ob-logo-box {
  width: 60px; height: 60px;
  background: rgba(255,255,255,0.18);
  border-radius: 14px;
  display: flex; align-items: center; justify-content: center;
  margin-bottom: 18px;
  border: 1px solid rgba(255,255,255,0.25);
  font-size: 30px;
  color: #fff;
}
.ob-brand-name {
  font-size: 24px;
  font-weight: 800;
  letter-spacing: 3px;
  margin: 0 0 6px;
  color: #fff;
}
.ob-brand-sub {
  font-size: 13px;
  color: rgba(255,255,255,0.7);
  margin: 0;
}

/* Feature list */
.ob-feats {
  list-style: none;
  margin: 0; padding: 0;
  display: flex; flex-direction: column; gap: 18px;
  position: relative; z-index: 1;
}
.ob-feat { display: flex; align-items: flex-start; gap: 12px; }
.ob-feat-ico {
  width: 38px; height: 38px; flex-shrink: 0;
  background: rgba(255,255,255,0.14);
  border-radius: 10px;
  display: flex; align-items: center; justify-content: center;
  font-size: 17px; color: #fff;
}
.ob-feat-t { font-size: 13px; font-weight: 600; color: #fff; }
.ob-feat-d { font-size: 12px; color: rgba(255,255,255,0.6); margin-top: 2px; }

/* Footer */
.ob-foot {
  font-size: 11px;
  color: rgba(255,255,255,0.4);
  margin: 0;
  position: relative; z-index: 1;
}

/* ─── Right Panel ──────────────────────────────────────── */
.ob-right {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  background: #fff;
  overflow-y: auto;
}

/* ─── Stepper ──────────────────────────────────────────── */
.ob-stepper {
  display: flex;
  align-items: flex-start;
  width: 100%;
  max-width: 400px;
  margin-bottom: 44px;
}
.ob-step {
  display: flex; flex-direction: column; align-items: center;
  flex-shrink: 0; width: 72px;
}
.ob-sdot {
  width: 36px; height: 36px;
  border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  font-size: 13px; font-weight: 700;
  background: #f3f4f6;
  color: #9ca3af;
  border: 2px solid #e5e7eb;
  transition: all 0.3s ease;
}
.ob-step--active .ob-sdot {
  background: #4f46e5;
  border-color: #4f46e5;
  color: #fff;
  box-shadow: 0 0 0 4px rgba(79,70,229,0.14);
}
.ob-step--done .ob-sdot {
  background: #10b981;
  border-color: #10b981;
  color: #fff;
}
.ob-slabel {
  font-size: 10px; font-weight: 600;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  margin-top: 6px;
  white-space: nowrap;
}
.ob-step--active .ob-slabel { color: #4f46e5; }
.ob-step--done .ob-slabel { color: #10b981; }
.ob-sline {
  flex: 1; height: 2px;
  background: #e5e7eb;
  margin-top: 17px; /* align with center of 36px dot */
  transition: background 0.35s ease;
}
.ob-sline--done { background: #10b981; }

/* ─── Panel (step content) ─────────────────────────────── */
.ob-panel {
  width: 100%;
  max-width: 400px;
  display: flex;
  flex-direction: column;
}
.ob-panel--c { align-items: center; text-align: center; }

/* Panel icon badge */
.ob-pico {
  width: 56px; height: 56px;
  border-radius: 14px;
  display: flex; align-items: center; justify-content: center;
  font-size: 26px;
  margin-bottom: 20px;
}
.ob-pico--indigo { background: #ede9fe; color: #4f46e5; }
.ob-pico--amber  { background: #fef3c7; color: #d97706; }
.ob-pico--lg { width: 60px; height: 60px; font-size: 28px; border-radius: 16px; }

.ob-ptitle {
  font-size: 24px; font-weight: 700;
  color: #111827;
  margin: 0 0 8px;
}
.ob-psub {
  font-size: 14px;
  color: #6b7280;
  margin: 0 0 28px;
  line-height: 1.6;
}
.ob-psub--sm { margin-bottom: 20px; }
.ob-psub--nb { margin-bottom: 0; }
.ob-mt4 { margin-top: 14px; }
.ob-mt5 { margin-top: 20px; }
.ob-mt6 { margin-top: 24px; }

/* ─── Form Fields ──────────────────────────────────────── */
.ob-fgroup { margin-bottom: 18px; }
.ob-flabel {
  display: block;
  font-size: 11px; font-weight: 700;
  color: #374151;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  margin-bottom: 7px;
}
.ob-finput {
  position: relative;
  display: flex; align-items: center;
  border: 1.5px solid #d1d5db;
  border-radius: 10px;
  background: #f9fafb;
  transition: border-color 0.2s, box-shadow 0.2s, background 0.2s;
}
.ob-finput:focus-within {
  border-color: #4f46e5;
  box-shadow: 0 0 0 3px rgba(79,70,229,0.1);
  background: #fff;
}
.ob-finput--err { border-color: #ef4444; }
.ob-finput--err:focus-within { box-shadow: 0 0 0 3px rgba(239,68,68,0.1); }
.ob-ficon {
  font-size: 17px; color: #9ca3af;
  padding: 0 10px 0 14px;
  flex-shrink: 0;
}
.ob-finput-el {
  flex: 1; height: 46px;
  background: transparent; border: none; outline: none;
  font-size: 14px; color: #111827;
  padding-right: 14px;
}
.ob-finput-el::placeholder { color: #c4c9d4; }
.ob-finput-el--pass { padding-right: 44px; }
.ob-eye {
  position: absolute; right: 10px;
  background: none; border: none; cursor: pointer;
  color: #9ca3af; font-size: 18px;
  padding: 4px;
  display: flex; align-items: center;
  transition: color 0.2s;
}
.ob-eye:hover { color: #4f46e5; }
.ob-textarea {
  display: block; width: 100%; box-sizing: border-box;
  padding: 12px 14px;
  border: 1.5px solid #d1d5db;
  border-radius: 10px; background: #f9fafb;
  font-size: 12px;
  font-family: 'Courier New', Courier, monospace;
  color: #111827; resize: vertical; outline: none;
  line-height: 1.55;
  transition: border-color 0.2s, box-shadow 0.2s, background 0.2s;
}
.ob-textarea:focus {
  border-color: #4f46e5;
  box-shadow: 0 0 0 3px rgba(79,70,229,0.1);
  background: #fff;
}
.ob-textarea::placeholder { color: #c4c9d4; }
.ob-ferr {
  font-size: 12px; color: #ef4444;
  margin: 6px 0 0;
  display: flex; align-items: center; gap: 4px;
}
.ob-fhint {
  font-size: 12px; color: #9ca3af;
  margin: 6px 0 0;
}

/* ─── Buttons ──────────────────────────────────────────── */
.ob-btn {
  display: flex; align-items: center; justify-content: center;
  gap: 8px; width: 100%;
  height: 48px; border-radius: 10px;
  font-size: 14px; font-weight: 600;
  cursor: pointer; border: none;
  transition: all 0.22s ease;
  letter-spacing: 0.2px;
}
.ob-btn:disabled { opacity: 0.5; cursor: not-allowed; transform: none !important; box-shadow: none !important; }
.ob-btn--primary {
  background: linear-gradient(135deg, #4f46e5 0%, #6d28d9 100%);
  color: #fff;
  box-shadow: 0 4px 14px rgba(79,70,229,0.3);
  margin-top: 8px;
}
.ob-btn--primary:not(:disabled):hover {
  background: linear-gradient(135deg, #4338ca 0%, #5b21b6 100%);
  box-shadow: 0 6px 20px rgba(79,70,229,0.42);
  transform: translateY(-1px);
}
.ob-btn--primary:not(:disabled):active {
  transform: translateY(0);
  box-shadow: 0 2px 8px rgba(79,70,229,0.28);
}
.ob-btn--ghost {
  background: transparent;
  color: #6b7280;
  font-size: 13px; font-weight: 500;
  height: 40px;
}
.ob-btn--ghost:hover { color: #4f46e5; background: #f5f3ff; }

/* ─── Method Switch ────────────────────────────────────── */
.ob-mswitch {
  display: flex;
  background: #f3f4f6;
  border-radius: 10px;
  padding: 4px; gap: 4px;
  margin-bottom: 24px;
}
.ob-mbtn {
  flex: 1; height: 38px;
  border-radius: 7px; border: none;
  background: transparent;
  font-size: 13px; font-weight: 500;
  color: #6b7280;
  cursor: pointer;
  display: flex; align-items: center; justify-content: center; gap: 6px;
  transition: all 0.2s ease;
}
.ob-mbtn .mdi { font-size: 15px; }
.ob-mbtn--on {
  background: #fff;
  color: #4f46e5; font-weight: 600;
  box-shadow: 0 1px 4px rgba(0,0,0,0.1), 0 0 0 1px rgba(79,70,229,0.08);
}

/* ─── Alert ────────────────────────────────────────────── */
.ob-alert { margin-bottom: 20px; border-radius: 8px !important; }

/* ─── Connecting / Pulse ───────────────────────────────── */
.ob-pulse {
  position: relative;
  width: 90px; height: 90px;
  display: flex; align-items: center; justify-content: center;
}
.ob-pr {
  position: absolute;
  width: 90px; height: 90px;
  border-radius: 50%;
  border: 2px solid rgba(79,70,229,0.35);
  animation: obPulse 2.2s ease-out infinite;
}
.ob-pr--2 { animation-delay: 0.9s; }
@keyframes obPulse {
  0% { transform: scale(0.7); opacity: 1; }
  100% { transform: scale(2.5); opacity: 0; }
}
.ob-loader {
  width: 220px; height: 4px;
  background: #e5e7eb;
  border-radius: 2px; overflow: hidden;
  margin-top: 28px;
}
.ob-loader-fill {
  height: 100%;
  background: linear-gradient(90deg, #4f46e5, #6d28d9, #4f46e5);
  background-size: 200%;
  animation: obLoader 1.6s ease-in-out infinite;
}
@keyframes obLoader {
  0% { width: 0%; margin-left: 0; }
  60% { width: 70%; margin-left: 0; }
  100% { width: 0%; margin-left: 100%; }
}

/* ─── Success ──────────────────────────────────────────── */
.ob-success {
  position: relative;
  width: 80px; height: 80px;
  display: flex; align-items: center; justify-content: center;
}
.ob-success-bg {
  position: absolute; inset: 0;
  border-radius: 50%; background: #d1fae5;
  animation: obPop 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards;
}
.ob-success-ico {
  font-size: 52px; color: #10b981;
  position: relative; z-index: 1;
  animation: obPop 0.5s 0.2s cubic-bezier(0.175, 0.885, 0.32, 1.275) both;
}
@keyframes obPop {
  0% { transform: scale(0); opacity: 0; }
  100% { transform: scale(1); opacity: 1; }
}
.ob-dots {
  display: flex; gap: 7px; margin-top: 24px;
}
.ob-dots span {
  width: 8px; height: 8px; border-radius: 50%;
  background: #4f46e5;
  animation: obBounce 1.2s ease-in-out infinite;
}
.ob-dots span:nth-child(2) { animation-delay: 0.15s; }
.ob-dots span:nth-child(3) { animation-delay: 0.3s; }
@keyframes obBounce {
  0%, 80%, 100% { transform: translateY(0); opacity: 0.4; }
  40% { transform: translateY(-8px); opacity: 1; }
}

/* ─── Spinner ──────────────────────────────────────────── */
.ob-spin { display: inline-block; animation: obSpin 0.9s linear infinite; }
@keyframes obSpin { to { transform: rotate(360deg); } }

/* ─── Transitions ──────────────────────────────────────── */
.ob-slide-enter-active,
.ob-slide-leave-active { transition: all 0.28s ease; }
.ob-slide-enter { opacity: 0; transform: translateX(24px); }
.ob-slide-leave-to { opacity: 0; transform: translateX(-24px); }

.ob-fade-enter-active,
.ob-fade-leave-active { transition: opacity 0.18s ease; }
.ob-fade-enter, .ob-fade-leave-to { opacity: 0; }
</style>
